import React from 'react';
import ReactDOM from 'react-dom';
import immutable from 'immutable';
import scrollIntoView from 'dom-scroll-into-view';

import ConstValues from 'utils/const';

export function componentHoc(WrappedComponent) {
  return class extends React.Component {
    constructor(props) {
      super(props);

      this.scrollWidgetIntoViewIfNecessary = this.scrollWidgetIntoViewIfNecessary.bind(this);
      this.getIconButtonInGroupDivStyle = this.getIconButtonInGroupDivStyle.bind(this);
      this.getIconButtonInGroupLabelStyle = this.getIconButtonInGroupLabelStyle.bind(this);
      this._getBorderStyleForIconButtonOption = this._getBorderStyleForIconButtonOption.bind(this);
      this._iconButtonInGroupDivStyle = this._iconButtonInGroupDivStyle.bind(this);
    }

    getStyle(style, props) {
      if (!immutable.Iterable.isIterable(style)) {
        style = immutable.Map(style);
      }
      // Skip all keys in `props` whose values are `NaN` or `null`
      return style
        .mergeWith((prev, next) => {
          if (next) {
            return next;
          }
          return prev;
        }, props)
        .toObject();
    }

    /**
     * When this.props.toggled is true, selects `this` and scrolls
     * the page such that the parent of `this` is aligned with the
     * bottom of the view area. For use with search widgets.
     *
     */
    scrollWidgetIntoViewIfNecessary() {
      if (!this.props.toggled) {
        return;
      }
      window.requestAnimationFrame(() => {
        const domNode = ReactDOM.findDOMNode(this);
        const rect = domNode.getBoundingClientRect();
        const shouldScroll = rect.top < 0 || rect.bottom > window.innerHeight;
        if (shouldScroll) {
          scrollIntoView(domNode, window, {
            alignWithTop: false,
            offsetBottom: 20,
          });
        }
      });
    }

    /**
     * Return a style object for the whole <div> of a <IconButton>
     * `opt` is currently active or not
     * @param  {object} opt   Option of the IconButton
     * @param {integer} idx Index of `opt` which is used to determine the `border-radius`
     * @return {object}       A style object with its backgroundColor updated
     * @param widthInlinePadding
     * @param highlightMode
     * @return {Object}
     */
    getIconButtonInGroupDivStyle(opt, idx, widthInlinePadding = 1, highlightMode = 'background') {
      let style = immutable.Map(iconButtonInGroupStyle).toObject();
      return this._iconButtonInGroupDivStyle(
        style,
        this.props.buttonOptions,
        opt,
        idx,
        widthInlinePadding,
        highlightMode
      );
    }

    /**
     * Return a new style with updated border-related properties for `opt` and `idx`
     * @param  {object} style A css style object
     * @param {integer} idx Index of `opt` which is used to determine the `border-radius`
     * @return {object}       A css style object
     */
    _getBorderStyleForIconButtonOption(style, idx) {
      if (idx === 0) {
        style.borderTopLeftRadius = ConstValues.stringSearchFieldDefaultOptionBorderRadius;
        style.borderBottomLeftRadius = ConstValues.stringSearchFieldDefaultOptionBorderRadius;
      } else if (idx === this.props.buttonOptions.count() - 1) {
        style.borderTopRightRadius = ConstValues.stringSearchFieldDefaultOptionBorderRadius;
        style.borderBottomRightRadius = ConstValues.stringSearchFieldDefaultOptionBorderRadius;
      }
      return style;
    }

    getIconButtonInGroupLabelStyle(
      label,
      idx,
      widthInlinePadding = 1,
      highlightMode = 'background'
    ) {
      let style = immutable.Map(iconButtonInGroupLabelStyle).toObject();
      return this._iconButtonInGroupDivStyle(
        style,
        this.props.buttonLabels,
        label,
        idx,
        widthInlinePadding,
        highlightMode
      );
    }

    _iconButtonInGroupDivStyle(style, options, opt, idx, widthInlinePadding, highlightMode) {
      style.width = 100 / options.count() - widthInlinePadding + '%';
      style = this._getBorderStyleForIconButtonOption(style, idx);
      if (opt.key === this.props.selectedOptionKey && highlightMode === 'background') {
        style.backgroundColor = ConstValues.stringSearchFieldActiveBackgroundColor;
      }
      return style;
    }

    render() {
      return (
        <WrappedComponent
          ref={this.props.getRef}
          getStyle={this.getStyle}
          scrollWidgetIntoViewIfNecessary={this.scrollWidgetIntoViewIfNecessary}
          getIconButtonInGroupDivStyle={this.getIconButtonInGroupDivStyle}
          getIconButtonInGroupLabelStyle={this.getIconButtonInGroupLabelStyle}
          {...this.props}
        />
      );
    }
  };
}

const iconButtonInGroupStyle = {
  border: '1px solid #dfdfdf',
  display: 'inline-block',
  backgroundColor: 'white',
  height: '34px',
  marginTop: '10px',
};

const iconButtonInGroupLabelStyle = {
  display: 'inline-block',
  backgroundColor: 'white',
  height: '34px',
  marginTop: '10px',
};
